import { Box } from "@chakra-ui/react"
import { useMemo, useRef } from "react"
import useEmbedResizeMessaging from "../../embed/useEmbedResizeMessaging"
import { normalizeParamColorHex } from "../../embed/utils"
import PackageBadge from "../PackageBadge"
import PackageCard from "../PackageCard"
import PackageTag from "../PackageTag"
import type { PackageVariant } from "../types"

const EMBED_MESSAGE_SOURCE = "pathwright:package-embed"

// "card" is the default variant for embeds
const parseVariantParam = (value: string | null): PackageVariant =>
  value === "tag" ? "tag" : value === "badge" ? "badge" : "card"

const parseButtonTargetParam = (value: string | null): "_top" | "_blank" =>
  value === "new_tab" ? "_blank" : "_top"

const parseColorModeParam = (
  value: string | null
): "light" | "dark" | undefined => {
  if (value === "light" || value === "dark") {
    return value
  }

  return undefined
}

const parseOptionalTextParam = (
  params: URLSearchParams,
  paramKey: string
): string | undefined => {
  const value = params.get(paramKey)?.trim()
  return value || undefined
}

type PackageEmbedProps = {
  search?: string
}

function PackageEmbed({ search }: PackageEmbedProps) {
  const searchString =
    search ?? (typeof window !== "undefined" ? window.location.search : "")
  const params = useMemo(
    () => new URLSearchParams(searchString),
    [searchString]
  )

  const variant = parseVariantParam(params.get("variant"))
  const actionButtonLabel = parseOptionalTextParam(params, "button_label")
  const actionButtonHref = parseOptionalTextParam(params, "button_link")
  const actionButtonTarget = parseButtonTargetParam(params.get("button_target"))

  const actionButton = {
    label: actionButtonLabel,
    href: actionButtonHref,
    target: actionButtonTarget
  }

  const primaryColor = normalizeParamColorHex(params.get("primary_color"))
  const backgroundColor = normalizeParamColorHex(params.get("background_color"))

  const themeOverrides = {
    primaryColor: primaryColor ?? undefined,
    backgroundColor: backgroundColor ?? undefined
  }
  const colorMode = parseColorModeParam(params.get("color_mode"))

  const containerRef = useRef<HTMLDivElement>(null)
  useEmbedResizeMessaging({ containerRef, source: EMBED_MESSAGE_SOURCE })

  return (
    <Box ref={containerRef} w="100%">
      {variant === "card" ? (
        <PackageCard
          themeOverrides={themeOverrides}
          actionButton={actionButton}
          colorMode={colorMode}
        />
      ) : variant === "tag" ? (
        <PackageTag themeOverrides={themeOverrides} colorMode={colorMode} />
      ) : variant === "badge" ? (
        <PackageBadge themeOverrides={themeOverrides} colorMode={colorMode} />
      ) : null}
    </Box>
  )
}

export default PackageEmbed
